// Server-side PDF download via Playwright (POST /api/pdf/generate).
//
// Why not window.print()?
// The previous implementation used window.print() and relied on the user
// choosing "Save as PDF" in the browser's print dialog. That flow:
//   - opened an intrusive print modal on desktop
//   - broke on iOS Safari (share-sheet only, no direct file save)
//   - was unreliable on Chrome Android
//
// New flow: we snapshot the rendered `.paper` element + every relevant
// `<style>` / `<link rel="stylesheet">` from the current document, package
// them into a self-contained HTML string with a `<base href>` pointing at
// the frontend origin (so relative asset URLs still resolve), and POST it
// to the backend. Playwright chromium renders it to a text-selectable,
// ATS-compatible PDF and returns bytes, which we push through a native
// `<a download>` click. Works identically on desktop + Android + iOS.

import axios from "axios";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Collects every stylesheet-relevant node from <head> so the backend
// Playwright render matches the on-screen preview exactly (Google Fonts,
// Tailwind bundle, index.css injected keyframes, ResumePreview.js scoped
// styles, etc.). We serialise the outerHTML of each so Playwright can
// re-parse them verbatim.
function collectHeadStyles() {
  const nodes = document.head.querySelectorAll(
    'link[rel="stylesheet"], style'
  );
  return Array.from(nodes).map((n) => n.outerHTML).join("\n");
}

// Builds a self-contained HTML document string wrapping the given paper
// element. A <base href> lets relative Tailwind/font URLs resolve inside
// the headless render context. Note the DELIBERATE absence of any
// `.paper { padding }` rule here: the browser-native `@page { margin }`
// set by the backend (2 cm for standard templates, 0 for full-bleed) is
// the SINGLE source of page-edge whitespace in print. Adding paper
// padding on top would double up the margin (screen preview measures
// `PAGE_TOP_MARGIN + PAGE_BOTTOM_MARGIN = 152px` of internal padding on
// .paper — @page's browser margin already reserves that same 152px
// natively), which was shrinking the printable area and pushing sections
// onto phantom extra pages.
function buildDocumentHtml(paperOuterHtml) {
  const origin = window.location.origin;
  const headStyles = collectHeadStyles();
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<base href="${origin}/" />
${headStyles}
<style>
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  /* Print-mode overrides — .paper has NO padding here. @page margin
     supplied by Playwright is the sole source of page-edge whitespace,
     matching the preview's PAGE_TOP_MARGIN + PAGE_BOTTOM_MARGIN = 152 px
     virtual reserve exactly (2 cm at 96 dpi). Also strips preview-only
     chrome so the PDF is clean. */
  .paper {
    padding: 0 !important;
    box-shadow: none !important;
    max-height: none !important;
    overflow: visible !important;
    user-select: text !important;
    -webkit-user-select: text !important;
    display: block !important;
    width: 100% !important;
    max-width: none !important;
    min-height: 0 !important;
    margin: 0 !important;
  }
  /* Hide preview-only chrome */
  [data-page-indicator], .watermark-overlay [data-testid^="watermark-diagonal"] { display: none !important; }
  /* Force page breaks at spacer sections — spacers themselves get their
     top margin from the browser's @page top margin (for standard
     templates) or from padding-top: 2 cm (for full-bleed templates,
     since their @page margin is 0). */
  section[data-pagebreak-spacer] {
    page-break-before: always;
    break-before: page;
    margin-top: 0 !important;
    padding-top: 0 !important;
  }
  .paper[data-template="two-column"] section[data-pagebreak-spacer],
  .paper[data-template="photo-header"] section[data-pagebreak-spacer],
  .paper[data-template="creative"] section[data-pagebreak-spacer] {
    padding-top: 2cm !important;
  }
</style>
</head>
<body>
${paperOuterHtml}
</body>
</html>`;
}

/**
 * Generates a PDF from the given paper element and triggers a native
 * browser file download. Works on desktop, Android, and iOS.
 *
 * @param {HTMLElement} paperEl   the `.paper` DOM node holding the resume
 * @param {string}      filename  suggested download filename (with .pdf)
 * @param {string}      pageFormat "a4" | "letter"
 * @param {boolean}     fullBleed true for TwoColumn / PhotoHeader / Creative
 */
export async function downloadPdf(paperEl, filename = "resume.pdf", pageFormat = "a4", fullBleed = false) {
  if (!paperEl) throw new Error("Resume preview element not available");

  // Clone the paper so we can strip preview-only inline styles
  // (min-height, max-height, overflow: hidden, flex layout) without
  // mutating the live DOM. These inline styles otherwise fight our
  // print-only CSS !important overrides and force the paper into a
  // fixed 1123 px box, causing a phantom blank page 2 whenever the
  // content itself happens to be slightly shorter than one A4 sheet.
  const clone = paperEl.cloneNode(true);
  clone.style.minHeight = "0";
  clone.style.maxHeight = "none";
  clone.style.overflow = "visible";
  clone.style.display = "block";
  clone.style.padding = "0";
  clone.style.margin = "0";
  clone.style.boxShadow = "none";
  clone.style.width = "100%";
  clone.style.maxWidth = "none";
  // Same treatment for the direct wrapper inside .paper — its flex layout
  // was used for preview-only page-indicator overlays.
  const innerWrapper = clone.querySelector(":scope > div:not(style)");
  if (innerWrapper) {
    innerWrapper.style.flex = "unset";
    innerWrapper.style.display = "block";
  }

  const html = buildDocumentHtml(clone.outerHTML);
  const pageSize = pageFormat === "letter" ? "letter" : "a4";

  let resp;
  try {
    resp = await axios.post(
      `${API}/pdf/generate`,
      { html, page_size: pageSize, full_bleed: fullBleed, filename },
      { responseType: "blob", timeout: 300_000 }
    );
  } catch (err) {
    // With `responseType: "blob"`, axios wraps error bodies in a Blob.
    // Parse it back to JSON so callers can surface the backend `detail`
    // instead of a generic "Network Error" / "Could not generate PDF".
    const blob = err?.response?.data;
    if (blob && typeof blob.text === "function") {
      try {
        const text = await blob.text();
        const parsed = JSON.parse(text);
        err.message = parsed?.detail || text || err.message;
      } catch (_) { /* leave original message */ }
    }
    throw err;
  }

  // Trigger native download via an anchor click — works on every browser
  // including iOS Safari (which blocks programmatic blob URLs opened via
  // window.location but honours click-triggered <a download>).
  const blob = new Blob([resp.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename.toLowerCase().endsWith(".pdf") ? filename : `${filename}.pdf`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Small delay so mobile Safari has time to hand the blob to its
    // download handler before we revoke the URL.
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }
}
